import React from 'react';
import {
  Paper, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, IconButton, Button,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Environment, OSType, Disk, Addon,
} from '../../types';
import {
  VMRow, SelectOption, NormalizedDefaultAddon,
  DISK_LIST_SX, HEADER_CELL_SX, TABLE_CELL_SX, SPACER_CELL_SX,
  COL, CELL_W, NO_SPIN_SX, rowErrorSX, BACKUP_OPTIONS,
} from './types';
import { formatCurrency, formatDiskSize } from '../../utils/formatCurrency';

interface VMsTableProps {
  vms: VMRow[];
  environments: Environment[];
  filteredOSTypes: OSType[];
  osTypes: OSType[];
  filteredDisks: Disk[];
  disks: Disk[];
  addons: Addon[];
  defaultAddons: NormalizedDefaultAddon[];
  pricingReady: boolean;
  // Handlers
  onUpdateVM: (id: number, field: string, value: unknown) => void;
  onDeleteVM: (id: number) => void;
  onAddDisk: (vmId: number) => void;
  onUpdateDisk: (vmId: number, idx: number, diskId: number | null) => void;
  onRemoveDisk: (vmId: number, idx: number) => void;
  // Price/availability helpers
  isVMRowAvailable: (vm: VMRow) => boolean;
  calculateVMCost: (vm: VMRow) => number;
  ensureCurrentOption: (options: SelectOption[], currentValue: number | string | null | undefined, makeLabel: (v: number | string) => string) => SelectOption[];
  getAvailableRAM: (cpu: number) => number[];
  cpuOptions: SelectOption[];
}

const VMsTable: React.FC<VMsTableProps> = ({
  vms, environments, filteredOSTypes, osTypes, filteredDisks, disks,
  addons, defaultAddons, pricingReady,
  onUpdateVM, onDeleteVM, onAddDisk, onUpdateDisk, onRemoveDisk,
  isVMRowAvailable, calculateVMCost, ensureCurrentOption, getAvailableRAM, cpuOptions,
}) => {
  if (vms.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, elevation: 2, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ p: 1, backgroundColor: 'grey.300', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Virtual Machines</Typography>
        {Array.from(new Set(vms.map(v => v.environmentId ?? 1))).map(envId => {
          const env = environments.find(e => e.EnvironmentId === envId);
          const envAddons = defaultAddons
            .filter(da => da.EnvironmentId === envId && da.ApplyToVMs)
            .map(da => addons.find(a => a.AddonId === da.AddonId)?.AddonName)
            .filter(Boolean)
            .join(', ');
          return (
            <Typography key={envId} variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              <strong>{env?.EnvironmentName} VMs include {env?.StandardStorageGB}GB standard storage{envAddons && ' and the following add-ons:'}</strong>
              {envAddons && ` ${envAddons}`}
            </Typography>
          );
        })}
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.NAME) }}>Name</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ENV) }}>Env</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.CPU) }}>CPU</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.RAM) }}>RAM</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.OS) }}>OS</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, minWidth: COL.STORAGE, textAlign: 'center' }}>Extra Storage</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...SPACER_CELL_SX }}></TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>Qty</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right' }}>Cost/mo</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ACT) }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {vms.map((vm) => {
              const vmAvailable = isVMRowAvailable(vm);
              const cpuOptsWithCurrent = ensureCurrentOption(cpuOptions, vm.cpu, (v) => String(v));
              const availableRAM: SelectOption[] = getAvailableRAM(vm.cpu).map((ram) => ({ value: ram, label: ram }));
              const ramOptsWithCurrent = ensureCurrentOption(availableRAM, vm.ram, (v) => String(v));
              const osOptions: SelectOption[] = filteredOSTypes.map((os) => ({ value: os.OSTypeId, label: os.OSName }));
              const osLabel = (val: number | string) => osTypes.find((o) => o.OSTypeId === Number(val))?.OSName ?? String(val);
              const osOptsWithCurrent = ensureCurrentOption(osOptions, vm.osTypeId, osLabel);
              const cost = (!pricingReady && vm.savedLineCost != null) ? vm.savedLineCost : calculateVMCost(vm);

              return (
                <TableRow key={vm.id} hover sx={!vmAvailable ? rowErrorSX : undefined}>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                    <TextField size="small" value={vm.name} onChange={(e) => onUpdateVM(vm.id, 'name', e.target.value)} placeholder="VM Name" fullWidth />
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ENV) }}>
                    <TextField select size="small" value={vm.environmentId ?? environments[0]?.EnvironmentId ?? 1} onChange={(e) => onUpdateVM(vm.id, 'environmentId', parseInt(e.target.value, 10))} fullWidth>
                      {environments.map((env) => (<MenuItem key={env.EnvironmentId} value={env.EnvironmentId}>{env.EnvironmentName}</MenuItem>))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.CPU) }}>
                    <TextField select size="small" value={vm.cpu ?? ''} onChange={(e) => onUpdateVM(vm.id, 'cpu', Number(e.target.value))} fullWidth>
                      {cpuOptsWithCurrent.map((opt) => <MenuItem key={`cpu-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.RAM) }}>
                    <TextField select size="small" value={vm.ram ?? ''} onChange={(e) => onUpdateVM(vm.id, 'ram', Number(e.target.value))} fullWidth>
                      {ramOptsWithCurrent.map((opt) => <MenuItem key={`ram-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS) }}>
                    <TextField select size="small" value={vm.osTypeId ?? ''} onChange={(e) => onUpdateVM(vm.id, 'osTypeId', Number(e.target.value))} fullWidth>
                      {osOptsWithCurrent.map((opt) => <MenuItem key={`os-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, minWidth: COL.STORAGE }}>
                    <Box sx={DISK_LIST_SX}>
                      {(vm.disks ?? []).map((diskId, idx) => {
                        const diskOptions: SelectOption[] = filteredDisks.map((d) => ({ value: d.DiskId, label: formatDiskSize(d.DiskSize) }));
                        const diskLabel = (val: number | string) => { const d = disks.find((disk) => disk.DiskId === Number(val)); return d ? formatDiskSize(d.DiskSize) : String(val); };
                        const diskOptsWithCurrent = ensureCurrentOption(diskOptions, diskId ?? '', diskLabel);
                        return (
                          <Box key={idx} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField select size="small" value={diskId ?? ''} onChange={(e) => onUpdateDisk(vm.id, idx, e.target.value ? Number(e.target.value) : null)} sx={{ width: 112 }}>
                              {diskOptsWithCurrent.length > 0
                                ? diskOptsWithCurrent.map((opt) => <MenuItem key={`vd-${idx}-${opt.value}`} value={opt.value} disabled={opt.disabled ?? false}>{opt.label}</MenuItem>)
                                : <MenuItem value="">None</MenuItem>}
                            </TextField>
                            <IconButton size="small" onClick={() => onRemoveDisk(vm.id, idx)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: '1rem' }} /></IconButton>
                          </Box>
                        );
                      })}
                      {(vm.disks ?? []).length < 3 && (
                        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />} onClick={() => onAddDisk(vm.id)} sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}>Add</Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={SPACER_CELL_SX}></TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>
                    <TextField type="number" size="small" value={vm.quantity} onChange={(e) => onUpdateVM(vm.id, 'quantity', parseInt(e.target.value) || 1)} inputProps={{ min: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {vmAvailable ? formatCurrency(cost) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ACT), textAlign: 'right' }}>
                    <IconButton size="small" color="error" onClick={() => onDeleteVM(vm.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default VMsTable;
